import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";
import { Filesystem, Directory } from "@capacitor/filesystem";
import {
  SecureStorage,
  PDF_DATA_URL_PREFIX,
  FileBackedPdfMetadata,
  isFileBackedPdfMetadata,
} from "./types";
import { UserInfo } from "../models/Interfaces";
import { infoLog, warnLog } from "../utils/logger";

// Crypto configuration (matches WebEncryptedStorage patterns)
const PDF_CRYPTO_OPTS = {
  AES_GCM_LENGTH: 256,
  IV_LENGTH: 12, // 96 bits for AES-GCM
};

// Directory for encrypted PDF cache files
const ENCRYPTED_PDF_CACHE_DIR = "encrypted_pdfs";

// ===== Crypto Helper Functions =====

/**
 * Generate a random AES-GCM-256 key for encrypting a single PDF
 */
async function generatePdfEncryptionKey(): Promise<CryptoKey> {
  return await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: PDF_CRYPTO_OPTS.AES_GCM_LENGTH },
    true, // extractable - we need to export it for storage
    ["encrypt", "decrypt"]
  );
}

/**
 * Generate a random IV for AES-GCM encryption
 */
function generateIV(): Uint8Array {
  return window.crypto.getRandomValues(new Uint8Array(PDF_CRYPTO_OPTS.IV_LENGTH));
}

/**
 * Export a CryptoKey to base64 string for storage
 */
async function exportKeyToBase64(key: CryptoKey): Promise<string> {
  const rawKey = await window.crypto.subtle.exportKey("raw", key);
  return arrayBufferToBase64(rawKey);
}

/**
 * Import a base64-encoded key back to CryptoKey
 */
async function importKeyFromBase64(base64Key: string): Promise<CryptoKey> {
  const keyBuffer = base64ToArrayBuffer(base64Key);
  return await window.crypto.subtle.importKey(
    "raw",
    keyBuffer,
    { name: "AES-GCM", length: PDF_CRYPTO_OPTS.AES_GCM_LENGTH },
    false, // not extractable - we only need to decrypt
    ["decrypt"]
  );
}

/**
 * Encrypt PDF binary data
 */
async function encryptPdfContent(
  data: ArrayBuffer,
  key: CryptoKey,
  iv: Uint8Array
): Promise<ArrayBuffer> {
  return await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
}

/**
 * Decrypt PDF binary data
 */
async function decryptPdfContent(
  encryptedData: ArrayBuffer,
  key: CryptoKey,
  iv: Uint8Array
): Promise<ArrayBuffer> {
  return await window.crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, encryptedData);
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBufferLike): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Generate a filesystem path for a cached encrypted PDF
 */
function generatePdfFilePath(storageKey: string): string {
  // Sanitize the key to be filesystem-safe
  const sanitized = storageKey.replace(/[^a-zA-Z0-9_-]/g, "_");
  return `${ENCRYPTED_PDF_CACHE_DIR}/${sanitized}.enc`;
}

export default class NativeEncryptedStorage implements SecureStorage {
  async updateCryptoKey(u: UserInfo, password: string): Promise<void> {
    // Unlike the web implementation, there is no need to manage the crypto key
    // due to SwiftKeychainWrapper and AndroidKeyStore managing encryption.
    return;
  }

  async clear(): Promise<void> {
    // Clean up all cached PDF files before clearing storage
    await this.clearAllCachedPdfs();
    await SecureStoragePlugin.clear();
  }

  async getItem<T>(key: string): Promise<T | null> {
    try {
      const result = await SecureStoragePlugin.get({ key });
      if (result.value) {
        const deserialized = await this.deserialize<T | FileBackedPdfMetadata>(result.value);

        if (isFileBackedPdfMetadata(deserialized)) {
          const pdfDataUrl = await this.readEncryptedPdf(deserialized);
          return pdfDataUrl as T;
        }

        // Legacy format or non-PDF data
        return deserialized as T;
      }
    } catch (error) {
      infoLog(`Failed to get item ${key} from native encrypted storage: ${error}`);
    }
    return null;
  }

  async setItem<T>(key: string, value: T): Promise<T> {
    // Check if this is a PDF that should be file-backed
    if (this.isPdfDataUrl(value)) {
      await this.writeEncryptedPdf(key, value as string);
      return value;
    }

    // Standard path for non-PDF data
    const serializedVal = await this.serialize(value);
    await SecureStoragePlugin.set({ key, value: serializedVal });
    return value;
  }

  async removeItem(key: string): Promise<void> {
    try {
      // First, check if this key has an associated cached PDF file
      const result = await SecureStoragePlugin.get({ key });
      if (result.value) {
        const deserialized = this.deserialize<unknown>(result.value);
        if (isFileBackedPdfMetadata(deserialized)) {
          await this.deleteEncryptedPdfFile(deserialized.filePath);
        }
      }

      await SecureStoragePlugin.remove({ key });
    } catch (error) {
      infoLog(`Failed to remove item ${key} from native encrypted storage: ${error}`);
    }
  }

  async keys(): Promise<string[]> {
    const result = await SecureStoragePlugin.keys();
    return result.value;
  }

  async unlock(password: string): Promise<boolean> {
    // There is no additional unlock step to gain access to native key store
    return true;
  }

  // ===== Private Helper Methods =====

  /**
   * Check if a value is a PDF data URL that should be file-backed
   */
  private isPdfDataUrl(value: unknown): boolean {
    return typeof value === "string" && value.startsWith(PDF_DATA_URL_PREFIX);
  }

  /**
   * Write a PDF to the filesystem with encryption, store metadata in SecureStorage
   */
  private async writeEncryptedPdf(key: string, pdfDataUrl: string): Promise<void> {
    try {
      // Extract base64 content from data URL
      const base64Content = pdfDataUrl.substring(PDF_DATA_URL_PREFIX.length);
      const pdfBinary = base64ToArrayBuffer(base64Content);

      // Generate encryption key and IV
      const encryptionKey = await generatePdfEncryptionKey();
      const iv = generateIV();

      // Encrypt the PDF content
      const encryptedData = await encryptPdfContent(pdfBinary, encryptionKey, iv);

      // Generate file path
      const filePath = generatePdfFilePath(key);

      // Ensure directory exists and write file
      await this.ensurePdfCacheDirectory();
      await Filesystem.writeFile({
        path: filePath,
        data: arrayBufferToBase64(encryptedData),
        directory: Directory.Cache,
      });

      // Store metadata in SecureStorage
      const metadata: FileBackedPdfMetadata = {
        __type: "FileBackedPdf",
        key: await exportKeyToBase64(encryptionKey),
        iv: arrayBufferToBase64(iv.buffer),
        filePath,
        version: 1,
      };

      const serializedMetadata = await this.serialize(metadata);
      await SecureStoragePlugin.set({ key, value: serializedMetadata });
    } catch (error) {
      warnLog(`Failed to write encrypted PDF for ${key}, falling back to direct storage: ${error}`);
      // Fall back to direct storage (current behavior)
      const serializedVal = await this.serialize(pdfDataUrl);
      await SecureStoragePlugin.set({ key, value: serializedVal });
    }
  }

  /**
   * Read and decrypt a file-backed PDF, returning the original data URL
   */
  private async readEncryptedPdf(metadata: FileBackedPdfMetadata): Promise<string | null> {
    try {
      // Read encrypted file from filesystem
      const fileResult = await Filesystem.readFile({
        path: metadata.filePath,
        directory: Directory.Cache,
      });

      // Decode the base64 file content to ArrayBuffer
      const encryptedData = base64ToArrayBuffer(fileResult.data as string);

      // Import the encryption key
      const encryptionKey = await importKeyFromBase64(metadata.key);

      // Decode the IV
      const iv = new Uint8Array(base64ToArrayBuffer(metadata.iv));

      // Decrypt the content
      const decryptedData = await decryptPdfContent(encryptedData, encryptionKey, iv);

      // Convert back to base64 and construct data URL
      const base64Content = arrayBufferToBase64(decryptedData);
      return PDF_DATA_URL_PREFIX + base64Content;
    } catch (error) {
      warnLog(`Failed to read encrypted PDF from ${metadata.filePath}: ${error}`);
      // Clean up corrupted/missing file reference
      await this.deleteEncryptedPdfFile(metadata.filePath);
      return null;
    }
  }

  /**
   * Delete an encrypted PDF file
   */
  private async deleteEncryptedPdfFile(filePath: string): Promise<void> {
    try {
      await Filesystem.deleteFile({
        path: filePath,
        directory: Directory.Cache,
      });
    } catch (error) {
      // Don't throw - file may already be deleted
    }
  }

  /**
   * Ensure the PDF cache directory exists
   */
  private async ensurePdfCacheDirectory(): Promise<void> {
    try {
      await Filesystem.mkdir({
        path: ENCRYPTED_PDF_CACHE_DIR,
        directory: Directory.Cache,
        recursive: true,
      });
    } catch (error) {
      // Directory may already exist, which is fine
    }
  }

  /**
   * Clear all cached PDF files (called during clear())
   */
  private async clearAllCachedPdfs(): Promise<void> {
    try {
      await Filesystem.rmdir({
        path: ENCRYPTED_PDF_CACHE_DIR,
        directory: Directory.Cache,
        recursive: true,
      });
    } catch (error) {
      // Directory may not exist, which is fine
    }
  }

  private async serialize<T>(val: T): Promise<string> {
    return JSON.stringify(val);
  }

  private async deserialize<T>(val: string): Promise<T> {
    return JSON.parse(val);
  }
}
